import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Globe2, ShieldCheck, Sparkles, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = ["Importer", "Distributor", "Brand Owner", "Contract Manufacturer", "Formulation House", "Research / Institutional Buyer", "Other"];
const APPLICATION_CATEGORIES = ["Food & Beverage", "Nutraceuticals", "Cosmetics", "Pharmaceuticals", "Diagnostics", "Research", "Multi-sector"];
const GRADES = ["E18", "E25", "E30", "E40", "Organic Spirulina", "Multiple Grades"];
const EXCLUSIVITY_OPTIONS = ["Yes", "No", "Open to discussion"];

const DistributorPortal = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    website: "",
    country: "",
    region_served: "",
    business_type: "Importer",
    application_category: "Food & Beverage",
    grades_of_interest: [],
    expected_annual_volume: "",
    target_launch_timeline: "",
    regulatory_needs: "",
    exclusivity_interest: "Open to discussion",
    message: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGrade = (g) =>
    setForm((f) => ({
      ...f,
      grades_of_interest: f.grades_of_interest.includes(g)
        ? f.grades_of_interest.filter((x) => x !== g)
        : [...f.grades_of_interest, g],
    }));

  const canSubmit = useMemo(() =>
    form.company_name && form.contact_person && form.email && form.country && form.business_type && form.application_category,
  [form]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/distributor-application`, form);
      setSuccess(true);
      toast.success("Your distributor application has been received.");
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again or email export@spiruva.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="page-distributor-portal" className="pt-[72px]">
        <section className="container-narrow py-20 md:py-32 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green text-brand-cream mb-8">
            <Check size={28} />
          </div>
          <p className="eyebrow mb-4">◦  Application Received</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-brand-black leading-tight tracking-tight">
            Thank you. <span className="italic text-brand-teal">Under review.</span>
          </h1>
          <p className="mt-6 text-brand-black/70 max-w-xl mx-auto leading-relaxed text-sm md:text-base">
            Your distributor application has been received for review as part of SPIRUVA's July 2027 launch-partner intake. Our team will respond if your market, application fit, and planning horizon align with the next onboarding phase.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-distributor-portal" className="pt-[72px]">
      {/* HERO */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="container-wide py-16 md:py-28 relative">
          <p className="eyebrow-light mb-5">◦  Global Distributor Portal</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            Partner for the <span className="italic text-brand-gold">July 2027 rollout.</span>
          </h1>
          <p className="mt-5 md:mt-6 text-brand-cream/75 max-w-2xl leading-relaxed text-sm md:text-base">
            SPIRUVA is reviewing distribution and market-entry partners across priority regions. This intake process is designed for importers, specialty ingredient distributors, formulation houses, and strategic channel partners preparing early access to phycocyanin and spirulina supply.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-cream/10 max-w-4xl">
            {[
              { icon: Sparkles, t: "Launch-stage allocation planning" },
              { icon: Globe2, t: "Regional market coordination" },
              { icon: ShieldCheck, t: "Technical onboarding" },
              { icon: Briefcase, t: "Territory review for strategic markets" },
            ].map((b) => (
              <div key={b.t} className="bg-brand-black px-5 py-5">
                <b.icon size={16} className="text-brand-gold" />
                <p className="text-xs text-brand-cream/80 mt-3 leading-relaxed">{b.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="container-wide py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
        <div className="lg:col-span-4">
          <p className="eyebrow mb-4">◦  Distributor Interest Form</p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-black tracking-tight leading-tight">
            Tell us about your <span className="italic text-brand-teal">market focus.</span>
          </h2>
          <p className="text-sm md:text-base text-brand-black/70 mt-5 leading-relaxed">
            Please share your market focus, application fit, and expected volume requirements so our team can review alignment for technical onboarding and July 2027 supply planning.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-brand-black/75">
            {[
              "Reviewed by SPIRUVA partner desk within 5 business days",
              "Confidential intake — NDA available on application",
              "Territory and exclusivity discussions on a case basis",
            ].map((x) => (
              <li key={x} className="flex gap-3"><Check size={16} className="text-brand-gold mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="distributor-form"
          className="lg:col-span-8 bg-white border border-brand-green/15 p-6 sm:p-8 md:p-12 space-y-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Company Name *" testid="d-input-company">
              <input type="text" required value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} className={INPUT_CLASS} data-testid="d-input-company" />
            </Field>
            <Field label="Contact Person *" testid="d-input-contact">
              <input type="text" required value={form.contact_person} onChange={(e) => setField("contact_person", e.target.value)} className={INPUT_CLASS} data-testid="d-input-contact" />
            </Field>
            <Field label="Work Email *">
              <input type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className={INPUT_CLASS} data-testid="d-input-email" />
            </Field>
            <Field label="Phone / WhatsApp">
              <input type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={INPUT_CLASS} data-testid="d-input-phone" />
            </Field>
            <Field label="Website">
              <input type="url" placeholder="https://" value={form.website} onChange={(e) => setField("website", e.target.value)} className={INPUT_CLASS} data-testid="d-input-website" />
            </Field>
            <Field label="Country *">
              <input type="text" required value={form.country} onChange={(e) => setField("country", e.target.value)} className={INPUT_CLASS} data-testid="d-input-country" />
            </Field>
            <Field label="Region Served" hint="e.g. EU · MENA · APAC">
              <input type="text" value={form.region_served} onChange={(e) => setField("region_served", e.target.value)} className={INPUT_CLASS} data-testid="d-input-region" />
            </Field>
            <Field label="Business Type *">
              <select required value={form.business_type} onChange={(e) => setField("business_type", e.target.value)} className={INPUT_CLASS} data-testid="d-input-business-type">
                {BUSINESS_TYPES.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Application Category *">
              <select required value={form.application_category} onChange={(e) => setField("application_category", e.target.value)} className={INPUT_CLASS} data-testid="d-input-application-category">
                {APPLICATION_CATEGORIES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </Field>
            <Field label="Expected Annual Volume" hint="e.g. 50–200 kg · 2 MT">
              <input type="text" value={form.expected_annual_volume} onChange={(e) => setField("expected_annual_volume", e.target.value)} className={INPUT_CLASS} data-testid="d-input-volume" />
            </Field>
            <Field label="Target Launch Timeline" hint="e.g. Q3 2027 / Q1 2028">
              <input type="text" value={form.target_launch_timeline} onChange={(e) => setField("target_launch_timeline", e.target.value)} className={INPUT_CLASS} data-testid="d-input-timeline" />
            </Field>
            <Field label="Interested in Territory Review?">
              <select value={form.exclusivity_interest} onChange={(e) => setField("exclusivity_interest", e.target.value)} className={INPUT_CLASS} data-testid="d-input-exclusivity">
                {EXCLUSIVITY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>

          <div>
            <Label>Grade Interest</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="d-grade-chips">
              {GRADES.map((g) => {
                const on = form.grades_of_interest.includes(g);
                return (
                  <button
                    key={g}
                    type="button"
                    data-testid={`d-grade-${g.replace(/\s+/g, '-')}`}
                    onClick={() => toggleGrade(g)}
                    className={`min-h-[44px] px-4 py-2 text-xs font-mono uppercase tracking-widest border transition ${on ? "bg-brand-green text-brand-cream border-brand-green" : "border-brand-green/25 text-brand-black/70 hover:border-brand-gold"}`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Regulatory / Documentation Requirements" hint="e.g. EU Organic, USDA NPOP, FDA dossier expectations">
            <textarea rows={3} value={form.regulatory_needs} onChange={(e) => setField("regulatory_needs", e.target.value)} className={TEXTAREA_CLASS} data-testid="d-input-regulatory" />
          </Field>

          <Field label="Partnership Notes" hint="Tell us about your channel, brand portfolio, or specific allocation expectations">
            <textarea rows={4} value={form.message} onChange={(e) => setField("message", e.target.value)} className={TEXTAREA_CLASS} data-testid="d-input-message" />
          </Field>

          <div className="pt-6 border-t border-brand-green/10 flex justify-end">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="d-submit"
              className="btn-primary disabled:opacity-50 min-h-[48px]"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting</> : <>Submit Distributor Interest <ArrowRight size={14} /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

const INPUT_CLASS = "w-full mt-2 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none transition text-sm font-body min-h-[44px]";
const TEXTAREA_CLASS = "w-full mt-2 px-4 py-3 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none transition text-sm font-body resize-none";

const Label = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{children}</span>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-brand-black/40 mt-1.5 font-mono">{hint}</p>}
  </div>
);

export default DistributorPortal;
